import { parse } from "date-fns";
import { type StaffRef, resolveStaffName } from "@/lib/staff-name-display";
import { getTimezone } from "@/lib/timezone";
import { zonedDateKey, zonedInstantOnDay, zonedWallMinutes } from "@/lib/timezone-dates";
import { NameDisplayOrder } from "@/types/enums/name-display-order";
import { Unit, UnitConversions } from "@/types/enums/unit";
import { MembershipTiny } from "@/types/inventory/member";
import { cx } from "./tailwind-utils";
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True when an ISO-ish datetime string already has a timezone designator
 * (`Z` or a numeric offset like `+00:00` / `-0400`).
 *
 * Python's `datetime.isoformat()` emits offsets (e.g. `+00:00`), not `Z`.
 * Blindly appending `Z` to those strings produces Invalid Date
 * (`...+00:00Z`).
 */
function hasTimezoneDesignator(dateString: string): boolean {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
}

/**
 * Coerce a string or Date into a Date.
 *
 * Naive backend timestamps (no designator) are treated as UTC by appending `Z`.
 * Strings that already include `Z` or a numeric offset are parsed as-is.
 */
function toClinicDate(dateString: string | Date): Date {
  if (typeof dateString !== "string") return dateString;
  if (hasTimezoneDesignator(dateString)) {
    return new Date(dateString);
  }
  return new Date(dateString + "Z");
}
function parseDateOnlyParts(dateString: string): {
  year: number;
  month: number;
  day: number;
} | null {
  const match = DATE_ONLY_PATTERN.exec(dateString);
  if (!match) return null;
  const [, yearString, monthString, dayString] = match;
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  // Only reject what cannot be a calendar year. This bound decides whether a
  // value is treated as a calendar day at all, so a narrow range silently
  // routes real dates — a pre-1900 date of birth — through instant handling,
  // which drops them a day west of UTC. Years under 100 still have to go:
  // Date.UTC maps 0-99 to 1900-1999, which the round-trip below would reject
  // anyway.
  if (year < 100) return null;
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (utcDate.getUTCFullYear() !== year || utcDate.getUTCMonth() !== month - 1 || utcDate.getUTCDate() !== day) {
    return null;
  }
  return {
    year,
    month,
    day
  };
}

/**
 * Parses an API calendar-day value (YYYY-MM-DD) as that exact day in UTC.
 * Use for formatting date-only fields; do not use for local date-picker state.
 */
export function dateOnlyStringToUTCDate(dateString: string): Date {
  const parts = parseDateOnlyParts(dateString);
  if (!parts) return new Date(NaN);
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
}

/**
 * Formats a date string or Date object into a human-readable date and time string.
 *
 * @param dateString - The date to format. Can be:
 *   - A string in ISO format (e.g., "2023-01-15T12:30:00" or "2023-01-15T12:30:00Z")
 *   - A string in custom format (requires customFormat parameter)
 *   - A Date object
 * @param shortenMonth - Whether to use abbreviated month names (e.g., "Jan" vs "January"). Defaults to true.
 * @param includeYear - Whether to include the year in the formatted date. Defaults to true.
 * @param customFormat - Optional format string that specifies the format of the input dateString (e.g., "yyyy-MM-dd").
 *                      Only used when dateString is a string that doesn't follow ISO format.
 * @param timeZone - Zone to display in. Defaults to the clinic timezone; pass an explicit zone for
 *                   times shown on someone else's clock, such as a location's.
 * @returns A formatted date and time string (e.g., "Jan 15, 2023, 12:30 PM" or "January 15, 12:30 PM" depending on parameters)
 */
export const formatDateTime = (dateString: string | Date | null | undefined, shortenMonth: boolean = true, includeYear: boolean = true, customFormat?: string, timeZone: string = getTimezone()): string => {
  if (!dateString) return "";
  let date: Date;
  if (typeof dateString === "string") {
    if (customFormat) {
      // Parse the string using the provided custom format
      date = parse(dateString, customFormat, new Date());
    } else {
      date = toClinicDate(dateString);
    }
  } else {
    date = dateString;
  }
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    timeZone,
    year: includeYear ? "numeric" : undefined,
    month: shortenMonth ? "short" : "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * Display options for `formatDate`.
 *
 * `timeZone` is excluded on purpose: the calendar-day pin sets it, and a caller
 * passing their own would silently undo it. Server code that must name a zone
 * explicitly passes it on the legacy positional signature instead, where the
 * pin still wins over it.
 */
export type FormatDateOptions = Omit<Intl.DateTimeFormatOptions, "timeZone"> & {
  /**
   * Assert that a `datetime` value is really a calendar day, and read it in
   * UTC so no timezone can shift it. Only for columns that hold a day the
   * backend stored with a meaningless time — see `formatDate`'s note.
   */
  calendarDay?: boolean;
  /** The clock an instant is read on; the clinic's by default. */
  timeZone?: string;
};

/**
 * Formats a date for display. The default formatter — reach for this one.
 *
 * For a **string** it reads the wire shape, which is the same distinction the
 * backend draws between a `date` and a `datetime` column:
 *
 *   - `"2026-01-15"` is a calendar day. It carries no time to resolve, so it
 *     keeps the day it names in every timezone.
 *   - `"2026-01-15T21:30:00Z"` is an instant, and resolves to whichever
 *     calendar day the viewer was on at that moment.
 *
 * A **Date is always treated as an instant**, because a Date carries no way to
 * say otherwise. That matters for values a person picked: date pickers emit the
 * chosen day at *browser-local midnight*, and resolving that against a timezone
 * override west of the browser rolls it back a day. Reduce a picked day with
 * `pickedDay` from `@/lib/clinic-clock` first:
 *
 *     formatDate(pickedDay(pickedDate))   // the day they clicked
 *     formatDate(new Date())                     // now, in the viewer's zone
 *
 * For a `datetime` column that is really a calendar day — imported records that
 * never had a time — pass `{ calendarDay: true }`. Value alone cannot
 * distinguish those from a real instant, so the call site has to assert it.
 *
 * @param dateString ISO string, `YYYY-MM-DD`, or Date; undefined yields ""
 * @param optionsOrShortenMonth `FormatDateOptions`, or the legacy
 *   `shortenMonth` boolean
 */
export function formatDate(dateString: string | Date | null | undefined, options?: FormatDateOptions): string;
export function formatDate(dateString: string | Date | null | undefined, shortenMonth?: boolean, includeYear?: boolean, customFormat?: string, includeWeekday?: boolean, timeZone?: string): string;
export function formatDate(dateString: string | Date | null | undefined, optionsOrShortenMonth: FormatDateOptions | boolean = true, includeYear: boolean = true, customFormat?: string, includeWeekday: boolean = false, timeZone?: string): string {
  if (!dateString) return "";
  const {
    calendarDay: assertedCalendarDay,
    timeZone: optionsTimeZone,
    ...explicitOptions
  } = typeof optionsOrShortenMonth === "object" ? optionsOrShortenMonth : {} as FormatDateOptions;
  const zone = timeZone ?? optionsTimeZone;
  // The assertion is not a display option: `{ calendarDay: true }` alone still
  // gets the default shape, not Intl's numeric fallback.
  const hasDisplayOptions = Object.keys(explicitOptions).length > 0;
  const shortenMonth = typeof optionsOrShortenMonth === "boolean" ? optionsOrShortenMonth : true;
  let date: Date;
  let isCalendarDay = false;
  if (typeof dateString === "string") {
    if (customFormat) {
      // Parse the string using the provided custom format
      date = parse(dateString, customFormat, new Date());
    } else {
      const calendarDay = dateOnlyStringToUTCDate(dateString);
      isCalendarDay = !isNaN(calendarDay.getTime());
      date = isCalendarDay ? calendarDay : toClinicDate(dateString);
    }
  } else if (dateString instanceof Date) {
    date = dateString;
  } else {
    // Anything else would reach getTime() below and throw. A cell that renders
    // empty beats a page that does not render.
    return "";
  }
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    ...(hasDisplayOptions ? explicitOptions : {
      weekday: includeWeekday ? "long" : undefined,
      year: includeYear ? "numeric" : undefined,
      month: shortenMonth ? "short" : "long",
      day: "numeric"
    }),
    // Last, so it wins: the calendar-day pin is the whole contract here, and a
    // caller-supplied timeZone spreading over it would silently undo it. An
    // explicit `timeZone` therefore steers only the instant branch — it is what
    // server route handlers pass, where `getTimezone()` has no provider state.
    timeZone: isCalendarDay || assertedCalendarDay ? "UTC" : zone ?? getTimezone()
  });
}

/** Short month in clinic TZ, e.g. "Jan" (for calendar badges). */
export function formatMonthShort(dateString: string | Date | undefined, timeZone: string = getTimezone()): string {
  if (!dateString) return "";
  return toClinicDate(dateString).toLocaleDateString("en-US", {
    timeZone,
    month: "short"
  });
}

/** Day-of-month number in clinic TZ, e.g. "15" (for calendar badges). */
export function formatDayNumber(dateString: string | Date | undefined, timeZone: string = getTimezone()): string {
  if (!dateString) return "";
  return toClinicDate(dateString).toLocaleDateString("en-US", {
    timeZone,
    day: "numeric"
  });
}

/** Month + year in clinic TZ, e.g. "January 2026". */
export function formatMonthYear(dateString: string | Date | undefined, timeZone: string = getTimezone()): string {
  if (!dateString) return "";
  let date: Date;
  if (typeof dateString === "string") {
    // Real instants (ISO): format in clinic TZ.
    date = toClinicDate(dateString);
  } else {
    // Month-cursor Dates are usually local midnight on day 1 (e.g. new Date(y, m, 1)).
    // Mid-month noon keeps the label on the intended month when clinic TZ is west
    // of the browser. Other Date values are real instants — format as-is.
    const looksLikeMonthCursor = dateString.getDate() === 1 && dateString.getHours() === 0 && dateString.getMinutes() === 0 && dateString.getSeconds() === 0;
    date = looksLikeMonthCursor ? new Date(dateString.getFullYear(), dateString.getMonth(), 15, 12, 0, 0) : dateString;
  }
  return date.toLocaleDateString("en-US", {
    timeZone,
    year: "numeric",
    month: "long"
  });
}

/** Short weekday in clinic TZ, e.g. "Mon". */
export function formatWeekdayShort(dateString: string | Date | undefined, timeZone: string = getTimezone()): string {
  if (!dateString) return "";
  return toClinicDate(dateString).toLocaleDateString("en-US", {
    timeZone,
    weekday: "short"
  });
}

/** Long weekday in clinic TZ, e.g. "Monday". */
export function formatWeekdayLong(dateString: string | Date | undefined): string {
  if (!dateString) return "";
  return toClinicDate(dateString).toLocaleDateString("en-US", {
    timeZone: getTimezone(),
    weekday: "long"
  });
}

/** A "YYYY-MM-DD" day with weekday and ordinal, e.g. "Sunday, Nov 8th, 2026". */
export function formatDayWithOrdinal(day: string): string {
  const [year,, dayOfMonth] = day.split("-").map(Number);
  const weekday = formatDate(day, {
    weekday: "long"
  });
  const month = formatDate(day, {
    month: "short"
  });
  return `${weekday}, ${month} ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}, ${year}`;
}
export const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) {
    return `${remainingSeconds} sec`;
  }
  return `${minutes} min ${remainingSeconds} sec`;
};

/**
 * Strips a phone number to its last 10 digits (US format).
 * Mirrors the backend's `clean_phone_number` in `common/phone_number.py`.
 */
export function cleanPhoneNumber(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, "").slice(-10);
}
export function formatPhoneNumber(phoneNumber?: string, includeCountryCode: boolean = false) {
  if (!phoneNumber || phoneNumber.trim() === "") return "";
  const digits = cleanPhoneNumber(phoneNumber);
  if (digits.length === 0) return "";
  const formattedNumber = `(${digits.slice(0, 3) || ""}${digits.length > 3 ? ") " : ""}${digits.slice(3, 6) || ""}${digits.length > 6 ? "-" : ""}${digits.slice(6)}`;
  if (includeCountryCode) {
    return `+1 ${formattedNumber}`;
  }
  return formattedNumber;
}

/** Stand-in shown where a patient's contact detail exists but this role may not read it. */
export const HIDDEN_CONTACT = "••• ••• ••••";

/**
 * Formats a patient's phone number, or a mask when the role can't see it.
 *
 * The API nulls phoneNumber for roles without `patients:contact_info:read` but keeps
 * hasPhoneNumber true, so the two cases are distinguishable: a patient with no number
 * on file still renders as blank, while a hidden one renders as dots. Actions keyed on
 * the patient id — opening the chat, starting a call — keep working either way.
 */
export function formatPhoneNumberOrHidden(patient: {
  phoneNumber?: string | null;
  hasPhoneNumber?: boolean;
}): string {
  if (patient.phoneNumber) return formatPhoneNumber(patient.phoneNumber);
  return patient.hasPhoneNumber ? HIDDEN_CONTACT : "";
}

/**
 * Whether to render a phone row at all.
 *
 * True when the patient has a number the caller can read OR one they can't —
 * a hidden number still gets a (masked, still-clickable) row. False only when
 * the patient genuinely has no number on file. Call sites that test
 * `patient.phoneNumber` directly collapse those two cases and silently drop the
 * row for gated staff.
 */
export function showsPhoneNumber(patient: {
  phoneNumber?: string | null;
  hasPhoneNumber?: boolean;
}): boolean {
  return Boolean(patient.phoneNumber || patient.hasPhoneNumber);
}

/** Email equivalent of {@link showsPhoneNumber}. */
export function showsEmail(patient: {
  email?: string | null;
  hasEmail?: boolean;
}): boolean {
  return Boolean(patient.email || patient.hasEmail);
}

/** Email equivalent of {@link formatPhoneNumberOrHidden}. */
export function formatEmailOrHidden(patient: {
  email?: string | null;
  hasEmail?: boolean;
}): string {
  if (patient.email) return patient.email;
  return patient.hasEmail ? HIDDEN_CONTACT : "";
}

/**
 * Formats a phone number to E.164 format for API calls (e.g., SignalWire)
 * @param phoneNumber - Phone number string in any format
 * @returns Phone number in E.164 format (+1XXXXXXXXXX) or empty string if invalid
 * @example
 * formatPhoneNumberE164("(555) 123-4567") // returns "+15551234567"
 * formatPhoneNumberE164("555-123-4567") // returns "+15551234567"
 * formatPhoneNumberE164("5551234567") // returns "+15551234567"
 */
export function formatPhoneNumberE164(phoneNumber?: string): string {
  if (!phoneNumber || phoneNumber.trim() === "") return "";

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Handle different input formats
  if (cleaned.length === 10) {
    // US number without country code
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    // US number with country code
    return `+${cleaned}`;
  } else if (cleaned.length > 11) {
    // International number - assume it already has country code
    return `+${cleaned}`;
  }

  // Invalid length - return empty string
  return "";
}
export function formatAddress(address?: string | null, city?: string | null, state?: string | null, zip?: string | null, addressLineTwo?: string | null, className?: string) {
  // Check if all address fields are empty/null
  if (!address && !city && !state && !zip) {
    return <p className={cx("text-muted-foreground", className)}>
        No address provided
      </p>;
  }

  // Handle null values with empty strings for display
  const safeAddress = address || "";
  const safeCity = city || "";
  const safeState = state ? state.toUpperCase() : "";
  const safeZip = zip || "";
  const safeAddressLineTwo = addressLineTwo || "";
  return <span className={cx("text-body capitalize text-foreground-secondary", className)}>
      {`${formatToHumanReadable(safeAddress)}${safeAddressLineTwo ? ", " + formatToHumanReadable(safeAddressLineTwo) : ""}`}
      {(safeAddress || safeAddressLineTwo) && (safeCity || safeState || safeZip) && <br />}
      {(safeCity || safeState || safeZip) && <span>{`${formatToHumanReadable(safeCity)}${safeCity && (safeState || safeZip) ? ", " : ""}${safeState}${(safeCity || safeState) && safeZip ? " " : ""}${safeZip}`}</span>}
    </span>;
}
export function formatName(firstName?: string | null, lastName?: string | null): string {
  if (firstName == null || firstName === undefined) return "Unknown";
  let name = firstName;
  if (lastName != null && lastName !== undefined) {
    name += " " + lastName;
  }

  // Process each word separately
  const words = name.split(/\s+/);
  const formattedWords = words.map(word => {
    // Handle hyphenated names
    if (word.includes("-")) {
      return word.split("-").map(part => formatNamePart(part)).join("-");
    }
    return formatNamePart(word);
  });
  return formattedWords.join(" ");
}

/**
 * A staff member's name, shown the way they chose (order, credentials). Pass
 * their id, or an object with it; the name comes from the staff list.
 */
export function formatStaffName(staff: StaffRef | null | undefined): string {
  const resolved = resolveStaffName(staff);
  if (resolved?.firstName == null) return "Unknown";
  const first = formatName(resolved.firstName.trim());
  const last = resolved.lastName != null ? formatName(resolved.lastName.trim()) : null;
  let name: string;
  switch (resolved.order) {
    case NameDisplayOrder.LAST_FIRST:
      name = last ? `${last}, ${first}` : first;
      break;
    case NameDisplayOrder.FIRST_ONLY:
      name = first;
      break;
    default:
      name = last != null ? `${first} ${last}` : first;
  }
  // Credentials are shown exactly as typed, so "NP" never becomes "Np".
  return resolved.credentials ? `${name}, ${resolved.credentials}` : name;
}

// Helper function to format a single part of a name
function formatNamePart(word: string): string {
  // If word is all uppercase, convert to lowercase first
  if (word === word.toUpperCase() && word.length > 1) {
    word = word.toLowerCase();
  } else {
    // Look for sequences of 2+ uppercase letters and convert them to lowercase
    let result = "";
    let upperCount = 0;
    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      const isUpper = char === char.toUpperCase() && char.match(/[A-Z]/);
      if (isUpper) {
        upperCount++;

        // If this is the start of a 2+ uppercase sequence
        if (upperCount === 2) {
          // Convert this and the previous uppercase to lowercase
          result = result.slice(0, -1) + word[i - 1].toLowerCase() + char.toLowerCase();
        }
        // If we're continuing a 2+ sequence
        else if (upperCount > 2) {
          result += char.toLowerCase();
        }
        // Single uppercase - keep as is
        else {
          result += char;
        }
      } else {
        upperCount = 0;
        result += char;
      }
    }
    word = result;
  }

  // Always capitalize the first letter of each word
  return word.charAt(0).toUpperCase() + word.slice(1);
}
export function formatCurrency(amountInCents: number): string {
  if (!amountInCents) {
    return "$0.00";
  }
  if (!Number.isInteger(amountInCents)) {
    console.warn("Amount must be in cents and an integer.");
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(amountInCents / 100);
}

/**
 * Formats a dollar amount (value already in dollars, e.g. from analytics ROUND(amount/100, 2)).
 * Use for analytics results where the SQL returns dollar values.
 * Exact decimal strings are rounded to cents without floating-point conversion
 * (half away from zero, matching Intl's default). Unsupported strings stay raw.
 * Explicit string signs, including plus and negative zero, are retained.
 */
export function formatCurrencyDollars(dollarAmount: number | string): string {
  if (typeof dollarAmount === "string") {
    const decimal = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(dollarAmount);
    if (!decimal) return dollarAmount;
    const [, sign, integer, fraction = ""] = decimal;
    let cents = BigInt(integer + fraction.slice(0, 2).padEnd(2, "0"));
    if (fraction.length > 2 && fraction[2] >= "5") {
      cents += BigInt(1);
    }
    const digits = cents.toString().padStart(3, "0");
    const grouped = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${sign}$${grouped}.${digits.slice(-2)}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(Number(dollarAmount));
}

/**
 * Cents → a bare two-decimal string with no currency symbol or separators
 * (e.g. 123456 → "1234.56").
 *
 * Use for spreadsheet/CSV export *values* — ExportDrawer `format: "currency"`
 * columns need a parseable number so the sheet can sum/sort/filter it and apply
 * its own currency display format. For on-screen display use `formatCurrency`,
 * which includes the "$" and thousands separators.
 */
export function centsToDecimalString(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Detects if a field name indicates a currency field (stored in cents)
 */
export function isCurrencyField(fieldName: string | undefined | null): boolean {
  if (!fieldName || typeof fieldName !== "string") {
    return false;
  }
  const currencyFields = ["total", "total_outstanding", "total_discount", "amount", "balance", "copay", "deductible", "coinsurance", "fee", "cost", "price", "payment_amount", "refund_amount", "adjustment_amount",
  // Item-related currency fields
  "item_price", "line_total", "charge_total", "charge_total_outstanding", "charged_price",
  // Tax and tip fields
  "tax_amount", "tip_amount"];
  return currencyFields.some(cf => fieldName.toLowerCase().includes(cf));
}

/**
 * Formats a value based on field type (currency detection)
 */
export function formatAnalyticsValue(value: number, fieldName: string | undefined | null): string {
  if (isCurrencyField(fieldName)) {
    return formatCurrency(value);
  }
  return value.toLocaleString();
}
export function formatQuantity(qty: number | null | undefined): string {
  if (qty == null || !Number.isFinite(qty)) return "0";
  return Number(qty.toFixed(2)).toString();
}
export function formatNumberNoDecimals(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount / 100);
}
export function formatNumberThousands(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount / 100000) + "K";
}
export function calculateRelativeDuration(createdAt: string | Date, precise = false) {
  const createdDate = new Date(createdAt);
  // Get current time
  const now = new Date();

  // Calculate time difference in milliseconds
  const diffMs = now.getTime() - createdDate.getTime();
  const isFuture = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  // Format times with hours and minutes
  const formatHoursMinutes = () => {
    const diffMins = Math.round(absDiffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    if (diffMins === 0) {
      return "now";
    }
    let timeText = "";
    if (diffHours > 0) {
      timeText = `${diffHours} hour${diffHours > 1 ? "s" : ""}`;
      if (remainingMins > 0) {
        timeText += ` ${remainingMins} min${remainingMins > 1 ? "s" : ""}`;
      }
    } else {
      timeText = `${diffMins} minute${diffMins !== 1 ? "s" : ""}`;
    }
    return isFuture ? `in ${timeText}` : `${timeText} ago`;
  };

  // If precise timing is requested or time is less than a day
  if (precise || absDiffMs < 1000 * 60 * 60 * 24) {
    return formatHoursMinutes();
  }

  // For standard formatting (less precise)
  // Calculate days difference for cases less than a month
  const daysDifference = absDiffMs / (1000 * 3600 * 24);

  // Calculate months difference for longer periods
  const monthsDiff = (now.getUTCFullYear() - createdDate.getUTCFullYear()) * 12 + (now.getUTCMonth() - createdDate.getUTCMonth());
  const daysDiff = now.getUTCDate() - createdDate.getUTCDate();
  const adjustedMonthsDiff = monthsDiff + (daysDiff < 0 ? -1 : 0);
  function formatPeriod(quantity: number, period: string) {
    const formattedQuantity = quantity > 0 ? Math.floor(Math.abs(quantity)) : Math.ceil(Math.abs(quantity));
    return `${formattedQuantity} ${period}${formattedQuantity === 1 ? "" : "s"}`;
  }

  // For future dates
  if (isFuture) {
    if (daysDifference > 365) return formatPeriod(daysDifference / 365, "year") + " from now";
    if (daysDifference >= 28) {
      if (Math.abs(adjustedMonthsDiff) === 0) return "less than a month from now";
      return formatPeriod(Math.abs(adjustedMonthsDiff), "month") + " from now";
    }
    if (daysDifference > 1) return formatPeriod(daysDifference, "day") + " from now";
    return "less than a day from now";
  }

  // For past dates (more than a day)
  if (daysDifference >= 1 && daysDifference < 28) return formatPeriod(daysDifference, "day") + " ago";
  if (adjustedMonthsDiff < 12) {
    if (adjustedMonthsDiff === 0) return "less than a month ago";
    return formatPeriod(adjustedMonthsDiff, "month") + " ago";
  }
  return formatPeriod(daysDifference / 365, "year") + " ago";
}
const CLINICAL_INTERVAL_DAYS_CUTOFF = 14;
const CLINICAL_INTERVAL_WEEKS_CUTOFF_DAYS = 183;
const AVERAGE_DAYS_PER_MONTH = 365.25 / 12;

/**
 * Relative time for treatment intervals: "9 days ago", "6 weeks ago",
 * "9 weeks, 5 days ago", "in 3 weeks, 2 days".
 *
 * Treatment cadences are set in weeks (tox every 12 weeks, peels every 4-6),
 * and a month is not a fixed number of days, so `calculateRelativeDuration`'s
 * "1 month ago" can't tell a provider whether a patient is due. Days are
 * counted between calendar days in the clinic's timezone, so an appointment
 * six weeks ago reads "6 weeks" whatever its time of day.
 *
 * Under a day falls back to hours/minutes. Past about six months it shows
 * months, then years, where a patient is clearly off-cadence. Those are
 * counted from the same day total, not by `calculateRelativeDuration`'s
 * calendar-month floor, which would read "5 months" the day after "26 weeks".
 */
export function calculateClinicalInterval(value: string | Date) {
  const date = new Date(value);
  const now = new Date();
  if (Math.abs(now.getTime() - date.getTime()) < 1000 * 60 * 60 * 24) {
    return calculateRelativeDuration(date);
  }
  const dayNumber = (d: Date) => Date.parse(`${toClinicDateKey(d)}T00:00:00Z`) / (1000 * 60 * 60 * 24);
  const signedDays = Math.round(dayNumber(now) - dayNumber(date));
  const days = Math.abs(signedDays);
  const isFuture = signedDays < 0;
  if (days === 0) return calculateRelativeDuration(date);
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;
  let text: string;
  if (days < CLINICAL_INTERVAL_DAYS_CUTOFF) {
    text = plural(days, "day");
  } else if (days < CLINICAL_INTERVAL_WEEKS_CUTOFF_DAYS) {
    const weeks = Math.floor(days / 7);
    const remainder = days % 7;
    text = plural(weeks, "week");
    if (remainder > 0) text += `, ${plural(remainder, "day")}`;
  } else if (days < 365) {
    text = plural(Math.floor(days / AVERAGE_DAYS_PER_MONTH), "month");
  } else {
    text = plural(Math.floor(days / 365), "year");
  }
  return isFuture ? `in ${text}` : `${text} ago`;
}

/**
 * Converts strings from camelCase, snake_case, or ALL_CAPS to a human-readable format.
 * - For camelCase strings, it inserts spaces before uppercase letters and capitalizes the first letter of each word.
 * - For snake_case or ALL_CAPS strings, it replaces underscores with spaces and capitalizes the first letter of each word.
 *
 * @param {string} value - The string to be converted to a human-readable format.
 * @returns {string} The human-readable version of the input string.
 */
export function formatToHumanReadable(value?: string | null): string {
  if (value === null || value === undefined) return "";
  const isAllCaps = value === value.toUpperCase();
  const hasUnderscore = value.includes("_");
  const hasSpaces = /\s/.test(value);
  const isCamelCase = !hasSpaces && /[a-z][A-Z]/.test(value);

  // Short acronyms (2-3 chars, all caps, no spaces/underscores) should stay as-is
  if (isAllCaps && !hasUnderscore && !hasSpaces && value.length < 4) {
    return value;
  }

  // snake_case or ALL_CAPS → replace underscores, lower-case then title-case
  if (isAllCaps || hasUnderscore) {
    return value.replace(/[_]+/g, " ").toLowerCase().replace(/(^|[\s-])([a-z])/g, (_, p1: string, p2: string) => p1 + p2.toUpperCase());
  }

  // camelCase (no spaces) → insert spaces between lower→upper boundaries, then title-case
  if (isCamelCase) {
    const withSpaces = value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return withSpaces.trim().toLowerCase().replace(/(^|[\s-])([a-z])/g, (_, p1: string, p2: string) => p1 + p2.toUpperCase());
  }

  // Default: already spaced or mixed-case strings → normalize to title-case per word
  return value.toLowerCase().replace(/(^|[\s-])([a-z])/g, (_, p1: string, p2: string) => p1 + p2.toUpperCase());
}
const BILLING_FREQUENCY_MAP: Record<string, string> = {
  DAILY: "day",
  WEEKLY: "week",
  BIWEEKLY: "2 weeks",
  MONTHLY: "mo",
  EVERY_FOUR_WEEKS: "4 weeks",
  EVERY_FIVE_WEEKS: "5 weeks",
  EVERY_SIX_WEEKS: "6 weeks",
  EVERY_TEN_WEEKS: "10 weeks",
  EVERY_TWELVE_WEEKS: "12 weeks",
  QUARTERLY: "quarter",
  SEMI_ANNUALLY: "6 mo",
  YEARLY: "year"
};
export function formatFrequency(freq: string): string {
  return BILLING_FREQUENCY_MAP[freq] ?? formatToHumanReadable(freq).toLowerCase();
}

/**
 * Formats question types for display with user-friendly names.
 * Maps technical types to more intuitive labels.
 *
 * @param {string} questionType - The question type enum value
 * @returns {string} User-friendly display name
 */
export function formatQuestionTypeDisplay(questionType?: string | null): string {
  if (!questionType) return "";
  const overrides: Record<string, string> = {
    TEXT: "Short Answer",
    TEXT_AREA: "Long Answer",
    SINGLE_SELECT: "Single Choice (Dropdown)",
    MULTI_SELECT: "Multiple Choice (Dropdown)",
    RADIAL_SINGLE_SELECT: "Single Choice (Show All)",
    RADIAL_MULTI_SELECT: "Multiple Choice (Show All)",
    MEDICATIONS: "Medication Doses"
  };
  return overrides[questionType] || formatToHumanReadable(questionType);
}
export function truncateId(id: string) {
  // Handle IDs with underscores (e.g., cal_123456789)
  if (id.includes("_")) {
    const parts = id.split("_");
    if (id.length > 10) {
      const start = parts[0];
      const end = id.substring(id.length - 4);
      return `${start}_...${end}`;
    }
    return id;
  }

  // Handle UUIDs and other long IDs without underscores
  if (id.length > 10) {
    const start = id.substring(0, 4);
    const end = id.substring(id.length - 4);
    return `${start}...${end}`;
  }
  return id;
}

/**
 * Returns a YYYY-MM-DD string for an instant in the clinic's timezone.
 * Use this when grouping or comparing dates by calendar day.
 *
 * A value that is already a calendar day passes through untouched — it has no
 * time to resolve, and re-reading it as an instant would shift it west of UTC.
 */
export function toClinicDateKey(date: Date | string): string {
  if (typeof date === "string") {
    if (DATE_ONLY_PATTERN.test(date)) return date;
    return toClinicDate(date).toLocaleDateString("en-CA", {
      timeZone: getTimezone()
    });
  }
  return date.toLocaleDateString("en-CA", {
    timeZone: getTimezone()
  });
}

/**
 * Returns a YYYY-MM-DD string for the UTC calendar day a value names.
 *
 * Use it for a `datetime` column that carries a calendar day. Parse through
 * `toClinicDate` rather than `new Date`: the API emits naive timestamps with no
 * designator, and bare `new Date` reads those as browser-local, so the UTC day
 * taken afterwards can be the wrong one.
 */
export function toUtcDateKey(date: Date | string): string {
  if (typeof date === "string" && DATE_ONLY_PATTERN.test(date)) return date;
  const instant = toClinicDate(date);
  if (isNaN(instant.getTime())) return "";
  return instant.toISOString().slice(0, 10);
}

/**
 * Returns "Today", "Tomorrow", or "In N days" for a future date,
 * using the clinic timezone for calendar-day comparison.
 */
export function formatDaysUntil(date: Date, tz: string = getTimezone()): string {
  const targetDay = date.toLocaleDateString("en-CA", {
    timeZone: tz
  });
  const now = new Date();
  const today = now.toLocaleDateString("en-CA", {
    timeZone: tz
  });
  const tomorrow = new Date(now.getTime() + 86400000).toLocaleDateString("en-CA", {
    timeZone: tz
  });
  if (targetDay === today) return "Today";
  if (targetDay === tomorrow) return "Tomorrow";
  const diffDays = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return `In ${diffDays} days`;
}
export function formatTime(date: string | Date | undefined, showPeriod: boolean = true,
// Defaults to the clinic timezone. Pass an explicit zone for times that are
// displayed in someone else's zone rather than the viewer's.
timeZone: string = getTimezone()) {
  if (!date) return "";
  const dateObject = typeof date === "string" ? toClinicDate(date) : date;
  if (!(dateObject instanceof Date) || isNaN(dateObject.getTime())) {
    return typeof date === "string" ? date : "";
  }
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  };
  let formattedTime = dateObject.toLocaleTimeString("en-US", options);
  if (!showPeriod) {
    formattedTime = formattedTime.replace(/\s?[AP]M/, "");
  }
  return formattedTime;
}
export const formatHour = (hour: number) => {
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 || 12;
  return `${displayHour} ${period}`;
};

// Formats a decimal hour count for display, e.g. 5 -> "5.00 hrs".
export const formatHours = (hours?: number | null) => `${(hours ?? 0).toFixed(2)} hrs`;

// Formats time left in minutes and seconds given seconds
export const formatTimeLeft = (timeLeft: number) => {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
export function formatEventTimeAndDay(startDate: Date, durationMinutes: number) {
  try {
    const tz = getTimezone();
    const start = new Date(startDate);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

    // Wall-clock time-of-day and weekday resolved in the effective clinic zone,
    // not the browser's, so availability strings match the clinic's clock.
    const fmt = (minutesFromMidnight: number) => {
      const h = Math.floor(minutesFromMidnight / 60);
      const m = minutesFromMidnight % 60;
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    };
    const startTime = fmt(zonedWallMinutes(start, tz));
    const endTime = fmt(zonedWallMinutes(end, tz));
    const day = formatWeekdayLong(start);
    return {
      startTime,
      endTime,
      day
    };
  } catch {
    return {
      startTime: "00:00",
      endTime: "00:00",
      day: "Unknown"
    };
  }
}
export function formatDateWithDay(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    timeZone: getTimezone(),
    weekday: "long",
    day: "2-digit",
    month: "short"
  });
}

/**
 * Converts a string from snake_case or kebab-case to camelCase
 * @param str The string to convert
 * @returns The camelCase version of the input string
 * @example
 * toCamelCase("hello_world") // returns "helloWorld"
 * toCamelCase("hello-world") // returns "helloWorld"
 * toCamelCase("last_4") // returns "last4"
 */
export const toCamelCase = (str: string): string => {
  return str.replace(/([-_][a-z0-9])/g, group => group.toUpperCase().replace("-", "").replace("_", ""));
};

/**
 * Recursively converts all keys in an object from snake_case to camelCase
 * @param obj The object whose keys need to be converted
 * @returns A new object with all keys converted to camelCase
 * @example
 * convertKeysToCamelCase({ hello_world: { nested_key: "value" } })
 * // returns { helloWorld: { nestedKey: "value" } }
 */
export const convertKeysToCamelCase = <T extends object,>(obj: T): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item));
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      const value = obj[key as keyof T];
      acc[camelKey] = value !== null && typeof value === "object" ? convertKeysToCamelCase(value) : value;
      return acc;
    }, {} as any);
  }
  return obj;
};

// Helper to format enum values for display
export const formatUnit = (unit?: Unit, singular?: boolean): string => {
  if (!unit) return singular ? "unit" : "units";
  return unit.split("_").map(word => {
    switch (word) {
      case "ML":
        return "mL";
      case "MCG":
        return "mcg";
      case "PER":
        return "per";
      case "G":
        return "g";
      case "MG":
        return "mg";
      case "IU":
        return "IU";
      default:
        {
          const lowercased = word.charAt(0) + word.slice(1).toLowerCase();
          if (singular) {
            // Handle irregular plurals first
            switch (lowercased) {
              case "Boxes":
                return "Box";
              case "Pieces":
                return "Piece";
              case "Patches":
                return "Patch";
              case "Pouches":
                return "Pouch";
              case "Inches":
                return "Inch";
              default:
                // Handle regular plurals ending in "es" or "s"
                if (lowercased.endsWith("es") || lowercased.endsWith("s")) {
                  return lowercased.slice(0, -1);
                }
                return lowercased;
            }
          }
          return lowercased;
        }
    }
  }).join(" ");
};

/**
 * Formats a list of strings into a human-readable string
 * @param list The list of strings to format
 * @param separator The separator to use between the strings
 * @param lastSeparator The separator to use between the last two strings
 * @returns The formatted string
 */
export const formatList = (list: string[], separator: string = ", ", lastSeparator: string = " and ") => {
  if (!list || list.length === 0) return "";
  if (list.length === 1) return list[0];
  return list.slice(0, -1).join(separator) + lastSeparator + list[list.length - 1];
};

/**
 * Converts height from centimeters to feet and inches
 * @param heightInCm Height in centimeters (string or number)
 * @returns Formatted string in feet and inches (e.g., "5'11\"")
 */
export function formatHeight(heightInCm?: string | number | null): string | null {
  if (!heightInCm) return null;

  // Convert to number if it's a string
  const heightValue = typeof heightInCm === "string" ? parseFloat(heightInCm) : heightInCm;

  // If parsing failed or value is invalid, return null
  if (isNaN(heightValue) || heightValue <= 0) return null;
  const {
    feet,
    inches
  } = UnitConversions.cmToFeetInches(heightValue);
  return `${feet}'${inches}"`;
}

/**
 * Converts weight from kilograms to pounds
 * @param weightInKg Weight in kilograms (string or number)
 * @returns Formatted string in pounds (e.g., "165 lbs")
 */
export function formatWeight(weightInKg?: string | number | null): string | null {
  if (!weightInKg) return null;
  const weightValue = typeof weightInKg === "string" ? parseFloat(weightInKg) : weightInKg;

  // Check if the weight value is valid
  if (isNaN(weightValue) || weightValue <= 0) {
    return null;
  }
  const weightInLbs = UnitConversions.kgToLbs(weightValue);
  if (weightInLbs === null) return null;
  return `${Number(weightInLbs.toFixed(1))} lbs`;
}
export function formatBp(systolic: number, diastolic: number): string | null {
  if (!systolic || !diastolic) return null;

  // Check if the bp values are valid
  if (isNaN(systolic) || isNaN(diastolic) || systolic <= 0 || diastolic <= 0) {
    return null;
  }
  return `${systolic.toFixed(0)}/${diastolic.toFixed(0)} mmHg`;
}

/**
 * Converts waist circumference from centimeters to inches
 * @param waistInCm Waist circumference in centimeters (string or number)
 * @returns Formatted string in inches (e.g., "32.5 in")
 */
export function formatWaistCircumference(waistInCm?: string | number | null): string | null {
  if (!waistInCm) return null;
  const waistValue = typeof waistInCm === "string" ? parseFloat(waistInCm) : waistInCm;

  // Check if the waist value is valid
  if (isNaN(waistValue) || waistValue <= 0) {
    return null;
  }
  const waistInInches = UnitConversions.cmToInches(waistValue);
  if (waistInInches === null) return null;
  return `${Number(waistInInches.toFixed(1))} in`;
}
export function formDataToModel<T>(formData: any): T {
  const result = {} as T;

  // Process each field in the form data
  for (const key in formData) {
    // Skip undefined, null values, and empty strings
    if (formData[key] === undefined || formData[key] === null || formData[key] === "") {
      // Don't add this property to result at all
      continue;
    }

    // Handle Date objects - keep them as Date objects
    if (formData[key] instanceof Date) {
      (result as any)[key] = formData[key];
      continue;
    }

    // Try to detect date fields by name and convert string dates to Date objects
    if (typeof formData[key] === "string" && (key.toLowerCase().includes("date") || key.toLowerCase().includes("time"))) {
      try {
        const date = new Date(formData[key]);
        // Only use the date if it's valid
        if (!isNaN(date.getTime())) {
          (result as any)[key] = date;
          continue;
        }
      } catch {
        // If date parsing fails, fall through to normal assignment
      }
    }

    // Normal assignment for all other values
    (result as any)[key] = formData[key];
  }
  return result;
}

/**
 * Calculates BMI (Body Mass Index) from weight and height
 * @param weightInKg Weight in kilograms
 * @param heightInCm Height in centimeters
 * @returns BMI as a number with 1 decimal place, or null if invalid inputs
 */
export function calculateBMI(weightInKg?: number | null, heightInCm?: number | null): number | null {
  if (!weightInKg || !heightInCm || weightInKg <= 0 || heightInCm <= 0) {
    return null;
  }

  // BMI formula: weight (kg) / height (m)²
  const heightInM = heightInCm / 100;
  const bmi = weightInKg / (heightInM * heightInM);
  return Number(bmi.toFixed(1));
}

/**
 * Creates a Date object from a date-only string without timezone conversion
 * Useful for DOB where we want the exact date regardless of timezone
 * @param dateString Date string in YYYY-MM-DD format
 * @returns Date object representing the local date
 */
/**
 * Splits a DATE answer into the date and time a picker edits, on the clock of
 * `timeZone` (the effective zone by default).
 *
 * A date-only question stores "YYYY-MM-DD" — a calendar value with no instant
 * attached — and that is returned untouched, so it can never shift across a
 * timezone. A question with `includeTime` stores a UTC instant, which is
 * read on that clock here: the same instant then reads the same wherever the
 * note is opened.
 *
 * Both shapes are accepted so a question that gains a time later still renders
 * the date-only answers recorded before the switch was turned on.
 */
export function splitDateAnswer(value: string | null | undefined, timeZone: string = getTimezone()): {
  date: string;
  time: string;
} {
  if (!value || typeof value !== "string") return {
    date: "",
    time: ""
  };
  if (!value.includes("T")) return {
    date: value,
    time: ""
  };
  const instant = new Date(value);
  if (isNaN(instant.getTime())) {
    // Unparseable: fall back to the calendar half so the answer still shows.
    return {
      date: value.split("T")[0] ?? "",
      time: ""
    };
  }
  const wallMinutes = zonedWallMinutes(instant, timeZone);
  const hours = String(Math.floor(wallMinutes / 60)).padStart(2, "0");
  const minutes = String(wallMinutes % 60).padStart(2, "0");
  return {
    date: zonedDateKey(instant, timeZone),
    time: `${hours}:${minutes}`
  };
}

/**
 * Joins the date and time from the pickers back into a stored DATE answer.
 *
 * With a time, the pair is read as wall-clock on `timeZone` (the effective zone
 * by default) and stored as a UTC instant, so the answer carries its zone
 * rather than depending on where it is read. Without one the value stays the
 * date-only calendar string, which is what keeps turning `includeTime` back off
 * from stranding answers the date-only readers cannot parse.
 */
export function joinDateAnswer(datePart: string, timePart: string, timeZone: string = getTimezone()): string | null {
  if (!datePart) return null;
  if (!timePart) return datePart;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  if ([year, month, day, hours, minutes].some(part => Number.isNaN(part))) {
    return datePart;
  }
  const instant = zonedInstantOnDay(new Date(year, month - 1, day), hours * 60 + minutes, timeZone);
  return isNaN(instant.getTime()) ? datePart : instant.toISOString();
}
export function fromDateOnlyString(dateString: string): Date {
  if (!dateString || typeof dateString !== "string") {
    return new Date(NaN); // Return invalid date for invalid input
  }
  const parts = parseDateOnlyParts(dateString);
  if (!parts) return new Date(NaN);

  // Create date at noon to avoid timezone issues for local date-picker state.
  return new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0);
}

/**
 * Calculates age based on date of birth
 * @param dateOfBirth Date of birth in string format (YYYY-MM-DD) or Date object
 * @returns Age in years as a number
 */
export function calculateAge(dateOfBirth: string | Date): number {
  const today = new Date();
  const birthDate = typeof dateOfBirth === "string" ? fromDateOnlyString(dateOfBirth) : dateOfBirth;
  const age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || m === 0 && today.getDate() < birthDate.getDate()) {
    return age - 1;
  } else {
    return age;
  }
}

/**
 * Checks if the given date of birth is today (birthday)
 * @param dateOfBirth Date of birth in string format (YYYY-MM-DD) or Date object
 * @returns True if it's the person's birthday today
 */
export function isBirthdayToday(dateOfBirth: string | Date): boolean {
  if (!dateOfBirth) return false;
  const today = new Date();
  const birthDate = typeof dateOfBirth === "string" ? fromDateOnlyString(dateOfBirth) : dateOfBirth;
  return today.getMonth() === birthDate.getMonth() && today.getDate() === birthDate.getDate();
}
export function formatRecurrencePattern(rrule: string, startDate: Date, durationMinutes: number, showTime: boolean = true, timeZone: string = getTimezone()): string {
  try {
    // Parse RRULE to get frequency information
    const ruleParts = rrule.split(";");
    const freqInfo = ruleParts.find(part => part.startsWith("FREQ="));
    const byDayInfo = ruleParts.find(part => part.startsWith("BYDAY="));
    const byMonthDayInfo = ruleParts.find(part => part.startsWith("BYMONTHDAY="));
    const bySetPosInfo = ruleParts.find(part => part.startsWith("BYSETPOS="));
    if (!freqInfo) {
      return "Invalid recurrence pattern";
    }
    const freq = freqInfo.replace("FREQ=", "");
    const intervalInfo = ruleParts.find(part => part.startsWith("INTERVAL="));
    const interval = intervalInfo ? parseInt(intervalInfo.replace("INTERVAL=", ""), 10) || 1 : 1;
    // "Weekly" for every week, "Every 2 weeks" once the pattern skips some
    const cadence = (everyLabel: string, unit: string) => interval > 1 ? `Every ${interval} ${unit}s` : everyLabel;
    const endTime = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    const timeRange = showTime ? ` ${formatTime(startDate, false, timeZone)}-${formatTime(endTime, false, timeZone)}` : "";
    const dayNames: Record<string, string> = {
      MO: "Mon",
      TU: "Tue",
      WE: "Wed",
      TH: "Thu",
      FR: "Fri",
      SA: "Sat",
      SU: "Sun"
    };

    // Day order for sorting (Sunday = 0, Monday = 1, etc.)
    const dayOrder: Record<string, number> = {
      SU: 0,
      MO: 1,
      TU: 2,
      WE: 3,
      TH: 4,
      FR: 5,
      SA: 6
    };
    const positionNames: Record<string, string> = {
      "1": "First",
      "2": "Second",
      "3": "Third",
      "4": "Fourth",
      "-1": "Last"
    };
    if (freq === "DAILY") {
      return `${cadence("Daily", "day")}${timeRange}`;
    } else if (freq === "WEEKLY") {
      if (byDayInfo) {
        const days = byDayInfo.replace("BYDAY=", "").split(",");
        // Sort days by week order (Sunday to Saturday)
        const sortedDays = days.sort((a, b) => {
          return (dayOrder[a] ?? 99) - (dayOrder[b] ?? 99);
        });
        const readableDays = sortedDays.map(d => dayNames[d] || d).join(", ");
        return `${cadence("Weekly", "week")} on ${readableDays}${timeRange}`;
      }
      return `${cadence("Weekly", "week")}${timeRange}`;
    } else if (freq === "MONTHLY") {
      if (byMonthDayInfo) {
        const day = byMonthDayInfo.replace("BYMONTHDAY=", "");
        return `${cadence("Monthly", "month")} on day ${day}${timeRange}`;
      } else if (bySetPosInfo && byDayInfo) {
        // Handle "First Monday", "Last Friday", etc.
        const position = bySetPosInfo.replace("BYSETPOS=", "");
        const day = byDayInfo.replace("BYDAY=", "");
        const positionName = positionNames[position] || position;
        const dayName = dayNames[day] || day;
        return `${cadence("Monthly", "month")} on ${positionName} ${dayName}${timeRange}`;
      }
      return `${cadence("Monthly", "month")}${timeRange}`;
    } else if (freq === "YEARLY") {
      return `${cadence("Yearly", "year")}${timeRange}`;
    }
    return `${freq}${timeRange}`;
  } catch {
    return "Invalid recurrence pattern";
  }
}
export function formatMembershipName(membership: MembershipTiny, className?: string) {
  // Use a default color if textColor is null or undefined
  // Default matches the first color in the curated palette
  const textColor = membership.textColor || "#3B82F6";
  return <span className={`relative inline-block ${className || ""}`}>
      {/* Base text - always visible */}
      <span style={{
      color: textColor
    }}>
        {membership.name}
      </span>
      {/* Shine effect overlay - only visible during animation */}
      <span aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `linear-gradient(90deg, transparent 0%, transparent 45%, rgba(255,255,255,0.5) 50%, transparent 55%, transparent 100%)`,
      backgroundSize: "200% 100%",
      backgroundPosition: "-200% 0%",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: "transparent",
      color: textColor,
      mixBlendMode: "screen",
      animation: "shine-once 2s ease-in-out 1 forwards"
    }} onAnimationEnd={e => {
      e.currentTarget.style.display = "none";
    }}>
        {membership.name}
      </span>
    </span>;
}
export const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 5280)} ft away`;
  } else if (distance < 10) {
    return `${distance.toFixed(1)} mi away`;
  } else {
    return `${Math.round(distance / 10) * 10} mi away`;
  }
};
export const getOrdinalSuffix = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};
const MS_PER_DAY = 1000 * 60 * 60 * 24;
export interface ExpiryInfo {
  text: string;
  isWarning: boolean;
}

/**
 * Formats an expiry date into human-readable text with warning status
 * @param expiryDate - ISO date string or null
 * @returns Object with text and isWarning flag, or null if no expiry date
 */
export function formatExpiryDate(expiryDate: string | null): ExpiryInfo | null {
  if (!expiryDate) return null;
  const date = new Date(expiryDate);
  const daysUntilExpiry = Math.ceil((date.getTime() - Date.now()) / MS_PER_DAY);
  if (daysUntilExpiry < 0) {
    return {
      text: "Expired",
      isWarning: true
    };
  }
  if (daysUntilExpiry <= 30) {
    return {
      text: `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? "s" : ""}`,
      isWarning: true
    };
  }
  return {
    text: `Expires ${formatDate(date, {
      calendarDay: true
    })}`,
    isWarning: false
  };
}